import { useState, useEffect, useRef } from "react";
import { useAuth } from "../App";
import { apiClient, API } from "../App";
import { toast } from "sonner";
import { User, Shield, Users, Mail, Clock, CheckCircle, XCircle, Send, Trash2, RefreshCw, MailCheck, Settings2, History, CalendarDays, Edit2, Save, UserPlus, AlertCircle, Wrench, Play, Camera, Upload, Loader2, Video } from "lucide-react";
import MeetingIntegrationsCard from "../components/meetings/MeetingIntegrationsCard";
import EmailSignatureTab from "../components/settings/EmailSignatureTab";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const Settings = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  
  // User management state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [processingUser, setProcessingUser] = useState(null);
  
  // Email Digest state
  const [digestSettings, setDigestSettings] = useState({ enabled: true, frequency: "weekly", send_day: 0, send_hour: 9 });
  const [digestHistory, setDigestHistory] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  
  // PTO Balance state
  const [ptoBalances, setPtoBalances] = useState([]);
  const [ptoYear, setPtoYear] = useState(new Date().getFullYear());
  const [editingPto, setEditingPto] = useState(null);
  const [ptoForm, setPtoForm] = useState({ total_days: 15, carry_over_days: 0, additional_days: 0 });
  const [savingPto, setSavingPto] = useState(false);
  
  // Migration state
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  const [migrationForm, setMigrationForm] = useState({
    deleteUsers: "",
    fixOrphanedBlocks: true,
    fixOrphanedResources: true,
    fixDisplayNames: true,
    migratePasswords: true,
    dryRun: true
  });
  
  // Activate users state
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activateRunning, setActivateRunning] = useState(false);
  const [activateResults, setActivateResults] = useState(null);
  const [activateForm, setActivateForm] = useState({
    names: "",
    temporaryPassword: "TempPass123!"
  });
  const [activateSource, setActivateSource] = useState("invitations"); // "invitations" or "resources"
  
  // Profile Photo state
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Archived users state
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [restoringUser, setRestoringUser] = useState(null);
  
  const USER_ROLES = ["COO", "Program Manager", "Production", "Sales", "Marketing PM"];

  useEffect(() => {
    if (user?.role === "COO") {
      fetchData();
      fetchDigestData();
      fetchPtoData();
    } else {
      setLoading(false);
    }
    // Your photo hangs off your own account, so there is nothing to look up
    // first — the <img> asks for it and falls back to the monogram on a 404.
    setProfilePhoto(user?.id ? `${API}/users/${user.id}/profile-image` : null);
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await apiClient.post(`/users/me/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Refresh photo with cache-busting
      setProfilePhoto(`${API}/users/${user.id}/profile-image?t=${Date.now()}`);
      toast.success("Profile photo updated");
    } catch (err) {
      console.error("Failed to upload photo:", err);
      toast.error(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Let the same file be picked again after a failed attempt.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, invitesRes, pendingRes] = await Promise.all([
        apiClient.get("/users").catch(() => ({ data: [] })),
        apiClient.get("/invites").catch(() => ({ data: [] })),
        apiClient.get("/users/pending").catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setPendingUsers(pendingRes.data);
    } finally {
      setLoading(false);
    }
  };
  
  // User management functions
  const handleDeleteUser = async (userId) => {
    setProcessingUser(userId);
    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success("User archived successfully. You can restore them from the Archived tab.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive user");
    } finally {
      setProcessingUser(null);
      setDeleteConfirm(null);
    }
  };
  
  const handleApproveUser = async (userId) => {
    setProcessingUser(userId);
    try {
      await apiClient.post(`/users/${userId}/approve`);
      toast.success("User approved successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    } finally {
      setProcessingUser(null);
    }
  };
  
  const handleRejectUser = async (userId) => {
    setProcessingUser(userId);
    try {
      await apiClient.post(`/users/${userId}/reject`);
      toast.success("User rejected and removed");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject user");
    } finally {
      setProcessingUser(null);
    }
  };
  
  const handleUpdateUserRole = async (userId, newRole) => {
    setProcessingUser(userId);
    try {
      await apiClient.put(`/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setProcessingUser(null);
      setEditingUserRole(null);
    }
  };
  
  const fetchPtoData = async () => {
    try {
      const res = await apiClient.get(`/pto-balances?year=${ptoYear}`);
      setPtoBalances(res.data.balances || []);
    } catch (e) {
      // Ignore
    }
  };
  
  const fetchArchivedUsers = async () => {
    setLoadingArchived(true);
    try {
      const res = await apiClient.get("/users/archived");
      setArchivedUsers(res.data || []);
    } catch (e) {
      console.error("Failed to fetch archived users", e);
      setArchivedUsers([]);
    } finally {
      setLoadingArchived(false);
    }
  };
  
  const handleRestoreUser = async (userId) => {
    setRestoringUser(userId);
    try {
      await apiClient.post(`/users/${userId}/restore`);
      toast.success("User restored successfully");
      fetchArchivedUsers();
      fetchData(); // Refresh active users list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore user");
    } finally {
      setRestoringUser(null);
    }
  };
  
  // Fetch archived users when tab changes
  useEffect(() => {
    if (activeTab === "archived" && user?.role === "COO") {
      fetchArchivedUsers();
    }
  }, [activeTab]);
  
  useEffect(() => {
    if (user?.role === "COO") {
      fetchPtoData();
    }
  }, [ptoYear]);
  
  const handleSavePto = async () => {
    if (!editingPto) return;
    setSavingPto(true);
    try {
      await apiClient.post("/pto-balances", {
        resource_id: editingPto.resource_id,
        year: ptoYear,
        total_days: parseFloat(ptoForm.total_days),
        carry_over_days: parseFloat(ptoForm.carry_over_days),
        additional_days: parseFloat(ptoForm.additional_days)
      });
      toast.success("PTO balance updated");
      setEditingPto(null);
      fetchPtoData();
    } catch (err) {
      toast.error("Failed to update PTO balance");
    } finally {
      setSavingPto(false);
    }
  };
  
  const fetchDigestData = async () => {
    try {
      const [settingsRes, historyRes] = await Promise.all([
        apiClient.get("/email-digest/settings").catch(() => ({ data: {} })),
        apiClient.get("/email-digest/history").catch(() => ({ data: { history: [] } }))
      ]);
      if (settingsRes.data) setDigestSettings(settingsRes.data);
      if (historyRes.data?.history) setDigestHistory(historyRes.data.history);
    } catch (e) {
      // Ignore errors
    }
  };
  
  const handleSendTestDigest = async () => {
    setSendingDigest(true);
    try {
      await apiClient.post("/email-digest/send-test", { recipient_email: testEmail || null });
      toast.success("Test digest sent! Check your email.");
      fetchDigestData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test digest");
    } finally {
      setSendingDigest(false);
    }
  };
  
  const handleSendAllDigests = async () => {
    setSendingDigest(true);
    try {
      const res = await apiClient.post("/email-digest/send-all");
      toast.success(`Sent ${res.data.sent} digests, ${res.data.failed} failed`);
      fetchDigestData();
    } catch (err) {
      toast.error("Failed to send digests");
    } finally {
      setSendingDigest(false);
    }
  };
  
  const handleUpdateDigestSettings = async (key, value) => {
    const newSettings = { ...digestSettings, [key]: value };
    setDigestSettings(newSettings);
    try {
      await apiClient.post("/email-digest/settings", newSettings);
      toast.success("Settings updated");
    } catch (err) {
      toast.error("Failed to update settings");
    }
  };

  // Migration handler
  const handleRunMigration = async () => {
    setMigrationRunning(true);
    setMigrationResults(null);
    try {
      // Parse user names from comma-separated string
      const userNames = migrationForm.deleteUsers
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      const response = await apiClient.post("/admin/run-migration", {
        delete_users: userNames,
        fix_orphaned_blocks: migrationForm.fixOrphanedBlocks,
        fix_orphaned_resources: migrationForm.fixOrphanedResources,
        fix_display_names: migrationForm.fixDisplayNames,
        migrate_passwords: migrationForm.migratePasswords,
        dry_run: migrationForm.dryRun
      });
      
      setMigrationResults(response.data);
      
      if (response.data.success) {
        if (migrationForm.dryRun) {
          toast.success("Dry run completed - review results below");
        } else {
          toast.success("Migration completed successfully!");
          fetchData(); // Refresh user list
        }
      } else {
        toast.error("Migration completed with errors");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
      setMigrationResults({ success: false, error: err.response?.data?.detail || "Unknown error" });
    } finally {
      setMigrationRunning(false);
    }
  };

  // Activate users handler
  const handleActivateUsers = async () => {
    setActivateRunning(true);
    setActivateResults(null);
    try {
      const names = activateForm.names
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      if (names.length === 0) {
        toast.error("Please enter at least one name");
        setActivateRunning(false);
        return;
      }
      
      // Use different endpoint based on source
      const endpoint = activateSource === "resources" 
        ? "/admin/activate-from-resources" 
        : "/admin/activate-invitations";
      
      const response = await apiClient.post(endpoint, {
        names: names,
        temporary_password: activateForm.temporaryPassword
      });
      
      setActivateResults(response.data);
      
      if (response.data.success) {
        toast.success(`Activated ${response.data.results.activated.length} user(s)!`);
        fetchData();
      } else {
        toast.error("Activation completed with errors");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
      setActivateResults({ success: false, error: err.response?.data?.detail || "Unknown error" });
    } finally {
      setActivateRunning(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await apiClient.post(`/invites/${inviteId}/resend`);
      toast.success("Invitation resent");
      fetchData();
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await apiClient.delete(`/invites/${inviteId}`);
      toast.success("Invitation cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "COO":
        return "bg-purple-100 text-purple-700";
      case "Program Manager":
        return "bg-blue-100 text-blue-700";
      case "Production":
        return "bg-green-100 text-green-700";
      case "Sales":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "accepted":
        return "bg-green-100 text-green-700";
      case "expired":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pendingInvites = invites.filter(i => i.status === "pending");
  const acceptedInvites = invites.filter(i => i.status === "accepted");

  return (
    <div className="max-w-4xl mx-auto" data-testid="settings-view">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="heading-1 text-foreground">Settings</h1>
        <p className="body-text mt-1">Manage your account and team</p>
      </div>

      {/* Tabs. The bar used to render for COO only; Email Signature is every
          user's own to set up, so the bar is now always shown and the
          administrative tabs are what's gated. */}
      <div className="flex items-center gap-1 p-1 bg-secondary/50 rounded-lg w-full sm:w-fit mb-8 overflow-x-auto">
        <button
          onClick={() => setActiveTab("profile")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${
            activeTab === "profile"
              ? "bg-white text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Profile
        </button>
        {user?.role === "COO" && (
          <>
            <button
              onClick={() => setActiveTab("team")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${
                activeTab === "team"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="team-tab"
            >
              Team
              {(pendingInvites.length > 0 || pendingUsers.length > 0) && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                  {pendingInvites.length + pendingUsers.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("email")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${
                activeTab === "email"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="email-tab"
            >
              Email Digest
            </button>
            <button
              onClick={() => setActiveTab("archived")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${
                activeTab === "archived"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="archived-tab"
            >
              Archived
            </button>
          </>
        )}
        <button
          onClick={() => setActiveTab("signature")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none whitespace-nowrap ${
            activeTab === "signature"
              ? "bg-white text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="signature-tab"
        >
          Email Signature
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div className="space-y-8">
          {/* Profile Photo Section */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Profile Photo</CardTitle>
                  <CardDescription>Upload a photo to personalize your profile</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {/* Photo Preview */}
                <div className="relative">
                  {profilePhoto ? (
                    <img 
                      src={profilePhoto} 
                      alt="Profile" 
                      onError={() => setProfilePhoto(null)}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center border-4 border-white shadow-lg">
                      <User className="w-10 h-10 text-slate-400" />
                    </div>
                  )}
                  {uploadingPhoto && (
                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                
                {/* Upload Button */}
                <div className="space-y-2">
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {profilePhoto ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG or GIF. Max size 5MB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Profile Section */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <User className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Profile</CardTitle>
                  <CardDescription>Your account information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Name</span>
                  <span className="text-sm font-medium">{user?.name}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Email</span>
                  <span className="text-sm font-medium">{user?.email}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Role</span>
                  <Badge className={`${getRoleBadgeClass(user?.role)} rounded-full`}>{user?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Meetings & calendar — per-user Zoom / Outlook connections */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Video className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Meetings &amp; Calendar</CardTitle>
                  <CardDescription>
                    Connect your own Zoom and Outlook so HQ can book client calls for you
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <MeetingIntegrationsCard returnTo="/settings" />
            </CardContent>
          </Card>

          {/* Permissions Section */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Shield className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Permissions</CardTitle>
                  <CardDescription>Your access level in the system</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {user?.role === "COO" && (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Full access to all programs and data</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Invite and manage team members</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>View financial data and reports</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Edit production time estimates</span>
                    </div>
                  </>
                )}
                {user?.role === "Program Manager" && (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Manage assigned programs</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Update deliverable status</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>View program timelines</span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>System Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>The Render Pros - Operations OS</p>
                <p>Version 1.0.0</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Tab */}
      {activeTab === "team" && user?.role === "COO" && (
        <div className="space-y-8">
          {/* Pending Account Approvals */}
          {pendingUsers.length > 0 && (
            <Card className="border-2 border-orange-200 bg-orange-50/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <UserPlus className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Pending Account Approvals</CardTitle>
                      <CardDescription>{pendingUsers.length} accounts awaiting approval</CardDescription>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={fetchData} className="rounded-full">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-orange-200">
                  {pendingUsers.map((pendingUser) => (
                    <div key={pendingUser.id} className="flex items-center justify-between py-4" data-testid={`pending-user-${pendingUser.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{pendingUser.name}</p>
                          <p className="text-xs text-muted-foreground">{pendingUser.email}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Registered: {new Date(pendingUser.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Role Selector */}
                        <Select
                          value={pendingUser.role}
                          onValueChange={(newRole) => handleUpdateUserRole(pendingUser.id, newRole)}
                          disabled={processingUser === pendingUser.id}
                        >
                          <SelectTrigger className="w-36 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {USER_ROLES.map((role) => (
                              <SelectItem key={role} value={role} className="text-xs">
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Button
                          size="sm"
                          onClick={() => handleApproveUser(pendingUser.id)}
                          disabled={processingUser === pendingUser.id}
                          className="h-8 bg-green-600 hover:bg-green-700"
                          data-testid={`approve-user-${pendingUser.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectUser(pendingUser.id)}
                          disabled={processingUser === pendingUser.id}
                          className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`reject-user-${pendingUser.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active Team Members */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Active Team Members</CardTitle>
                    <CardDescription>{users.length} members</CardDescription>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowActivateDialog(true)}
                    className="gap-2"
                    data-testid="activate-users-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                    Activate Users
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMigrationDialog(true)}
                    className="gap-2"
                    data-testid="run-migration-btn"
                  >
                    <Wrench className="w-4 h-4" />
                    Run Migration
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No team members yet</p>
              ) : (
                <div className="divide-y divide-border">
                  {users.map((member) => (
                    <div key={member.id} className="flex items-center justify-between py-4" data-testid={`team-member-${member.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {member.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Role change for non-self users */}
                        {member.id !== user?.id ? (
                          <Select
                            value={member.role}
                            onValueChange={(newRole) => handleUpdateUserRole(member.id, newRole)}
                            disabled={processingUser === member.id}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {USER_ROLES.map((role) => (
                                <SelectItem key={role} value={role} className="text-xs">
                                  {role}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`${getRoleBadgeClass(member.role)} rounded-full text-xs`}>
                            {member.role}
                          </Badge>
                        )}
                        
                        {member.id === user?.id ? (
                          <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">You</Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(member)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            title="Delete user"
                            data-testid={`delete-user-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Invitations */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-yellow-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Pending Invitations</CardTitle>
                    <CardDescription>{pendingInvites.length} pending</CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={fetchData} className="rounded-full">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendingInvites.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No pending invitations</p>
              ) : (
                <div className="divide-y divide-border">
                  {pendingInvites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-yellow-50 border border-yellow-200 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{invite.name}</p>
                          <p className="text-xs text-muted-foreground">{invite.email}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Expires: {new Date(invite.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getRoleBadgeClass(invite.role)} rounded-full text-xs`}>
                          {invite.role}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResendInvite(invite.id)}
                          className="h-8 w-8 p-0"
                          title="Resend invitation"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Cancel invitation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invitation History */}
          {acceptedInvites.length > 0 && (
            <Card className="border border-border">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Accepted Invitations</CardTitle>
                    <CardDescription>Recently joined</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-border">
                  {acceptedInvites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{invite.name}</p>
                          <p className="text-xs text-muted-foreground">{invite.email}</p>
                        </div>
                      </div>
                      <Badge className={`${getRoleBadgeClass(invite.role)} rounded-full text-xs`}>
                        {invite.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Email Digest Tab */}
      {activeTab === "email" && (
        <div className="space-y-8">
          {/* Send Digest Card */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Send Weekly Digest</CardTitle>
                  <CardDescription>Send summary emails to PMs</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  📧 Weekly digests include: active programs summary, high priority items, deliverables due, and active blockers.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium mb-2 block">Test Email (optional)</Label>
                  <Input
                    placeholder="Override recipient email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={handleSendTestDigest}
                    disabled={sendingDigest}
                    variant="outline"
                    data-testid="send-test-digest-btn"
                  >
                    {sendingDigest ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MailCheck className="w-4 h-4 mr-2" />}
                    Send Test
                  </Button>
                  <Button
                    onClick={handleSendAllDigests}
                    disabled={sendingDigest}
                    data-testid="send-all-digests-btn"
                  >
                    {sendingDigest ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send to All PMs
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Settings Card */}
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Settings2 className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Digest Settings</CardTitle>
                  <CardDescription>Configure automatic email digests</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Enable Automatic Digests</Label>
                  <p className="text-sm text-muted-foreground">Send digests automatically on schedule</p>
                </div>
                <Switch
                  checked={digestSettings.enabled}
                  onCheckedChange={v => handleUpdateDigestSettings("enabled", v)}
                />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Frequency</Label>
                  <Select
                    value={digestSettings.frequency}
                    onValueChange={v => handleUpdateDigestSettings("frequency", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {digestSettings.frequency === "weekly" && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Send Day</Label>
                    <Select
                      value={String(digestSettings.send_day)}
                      onValueChange={v => handleUpdateDigestSettings("send_day", parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Monday</SelectItem>
                        <SelectItem value="1">Tuesday</SelectItem>
                        <SelectItem value="2">Wednesday</SelectItem>
                        <SelectItem value="3">Thursday</SelectItem>
                        <SelectItem value="4">Friday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Send Time</Label>
                  <Select
                    value={String(digestSettings.send_hour)}
                    onValueChange={v => handleUpdateDigestSettings("send_hour", parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7:00 AM</SelectItem>
                      <SelectItem value="8">8:00 AM</SelectItem>
                      <SelectItem value="9">9:00 AM</SelectItem>
                      <SelectItem value="10">10:00 AM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* History Card */}
          {digestHistory.length > 0 && (
            <Card className="border border-border">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <History className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Send History</CardTitle>
                    <CardDescription>Recent digest emails</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-border">
                  {digestHistory.slice(0, 10).map((item, idx) => (
                    <div key={item.id || idx} className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{item.recipient_email}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(item.sent_at).toLocaleString()} • {item.programs_count} programs
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">Sent</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Archived Users Tab */}
      {activeTab === "archived" && user?.role === "COO" && (
        <div className="space-y-6">
          <Card className="border border-border">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <History className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                </div>
                <div>
                  <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Archived Users</CardTitle>
                  <CardDescription>Former team members whose data is preserved for historical records</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingArchived ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : archivedUsers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No archived users</p>
                  <p className="text-sm mt-2">When you archive a user, they will appear here with their historical data preserved</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {archivedUsers.map(archivedUser => (
                    <div 
                      key={archivedUser.id || archivedUser.user_id} 
                      className="p-4 border rounded-lg bg-muted/30"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium">{archivedUser.name}</p>
                            <p className="text-sm text-muted-foreground">{archivedUser.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {archivedUser.role}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreUser(archivedUser.id || archivedUser.user_id)}
                            disabled={restoringUser === (archivedUser.id || archivedUser.user_id)}
                            data-testid={`restore-user-${archivedUser.id || archivedUser.user_id}`}
                          >
                            {restoringUser === (archivedUser.id || archivedUser.user_id) ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-1" />
                            )}
                            Restore
                          </Button>
                        </div>
                      </div>
                      
                      {/* Historical Data Summary */}
                      {archivedUser.historical_data && (
                        <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t">
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-lg font-semibold">{archivedUser.historical_data.cost_logs_count || 0}</p>
                            <p className="text-xs text-muted-foreground">Cost Logs</p>
                          </div>
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-lg font-semibold">{archivedUser.historical_data.time_entries_count || 0}</p>
                            <p className="text-xs text-muted-foreground">Time Entries</p>
                          </div>
                          <div className="text-center p-2 bg-background rounded">
                            <p className="text-lg font-semibold">{archivedUser.historical_data.assignments_count || 0}</p>
                            <p className="text-xs text-muted-foreground">Assignments</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Archived Date */}
                      {archivedUser.archived_at && (
                        <p className="text-xs text-muted-foreground mt-3">
                          Archived on {new Date(archivedUser.archived_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Invitations are handled in Team tab */}

      {/* Email Signature Tab — available to every user, not just COO */}
      {activeTab === "signature" && <EmailSignatureTab user={user} />}

      {/* Edit PTO Dialog */}
      <Dialog open={!!editingPto} onOpenChange={() => setEditingPto(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit PTO Balance - {editingPto?.resource_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Annual PTO Days</Label>
              <Input
                type="number"
                step="0.5"
                value={ptoForm.total_days}
                onChange={e => setPtoForm({...ptoForm, total_days: e.target.value})}
              />
              <p className="text-xs text-muted-foreground mt-1">Standard allocation for the year</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Carry Over Days</Label>
              <Input
                type="number"
                step="0.5"
                value={ptoForm.carry_over_days}
                onChange={e => setPtoForm({...ptoForm, carry_over_days: e.target.value})}
              />
              <p className="text-xs text-muted-foreground mt-1">Days carried from previous year</p>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Bonus/Adjustment Days</Label>
              <Input
                type="number"
                step="0.5"
                value={ptoForm.additional_days}
                onChange={e => setPtoForm({...ptoForm, additional_days: e.target.value})}
              />
              <p className="text-xs text-muted-foreground mt-1">Additional days granted (can be negative)</p>
            </div>
            
            <Separator />
            
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium">
                Total Available: {parseFloat(ptoForm.total_days || 0) + parseFloat(ptoForm.carry_over_days || 0) + parseFloat(ptoForm.additional_days || 0)} days
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPto(null)}>Cancel</Button>
            <Button onClick={handleSavePto} disabled={savingPto}>
              {savingPto ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive <strong>{deleteConfirm?.name}</strong> ({deleteConfirm?.email})? 
              Their data will be preserved for historical records, and they can be restored later from the Archived tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteUser(deleteConfirm?.id)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-delete-user"
            >
              Archive User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Run Data Migration
            </DialogTitle>
            <DialogDescription>
              Fix common data issues like orphaned records, missing names, and delete specific users.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Users to delete */}
            <div className="space-y-2">
              <Label>Users to Delete (comma-separated names)</Label>
              <Input
                placeholder="e.g., Sofia Lopez Ayala, Eugenia Haidar"
                value={migrationForm.deleteUsers}
                onChange={(e) => setMigrationForm({ ...migrationForm, deleteUsers: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Enter exact names of users you want to delete</p>
            </div>
            
            {/* Fix options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Fix Orphaned Blocks</Label>
                  <p className="text-xs text-muted-foreground">Remove assignment blocks with invalid artist references</p>
                </div>
                <Switch
                  checked={migrationForm.fixOrphanedBlocks}
                  onCheckedChange={(v) => setMigrationForm({ ...migrationForm, fixOrphanedBlocks: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Fix Orphaned Resources</Label>
                  <p className="text-xs text-muted-foreground">Remove Project Manager resources without user accounts</p>
                </div>
                <Switch
                  checked={migrationForm.fixOrphanedResources}
                  onCheckedChange={(v) => setMigrationForm({ ...migrationForm, fixOrphanedResources: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Fix Display Names</Label>
                  <p className="text-xs text-muted-foreground">Fix resources with missing display names</p>
                </div>
                <Switch
                  checked={migrationForm.fixDisplayNames}
                  onCheckedChange={(v) => setMigrationForm({ ...migrationForm, fixDisplayNames: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Migrate Passwords</Label>
                  <p className="text-xs text-muted-foreground">Migrate legacy password fields</p>
                </div>
                <Switch
                  checked={migrationForm.migratePasswords}
                  onCheckedChange={(v) => setMigrationForm({ ...migrationForm, migratePasswords: v })}
                />
              </div>
            </div>
            
            {/* Dry run option */}
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <Label className="text-amber-800">Dry Run Mode</Label>
                <p className="text-xs text-amber-600">Preview changes without making them</p>
              </div>
              <Switch
                checked={migrationForm.dryRun}
                onCheckedChange={(v) => setMigrationForm({ ...migrationForm, dryRun: v })}
              />
            </div>
            
            {/* Results display */}
            {migrationResults && (
              <div className={`p-4 rounded-lg border ${migrationResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`font-semibold text-sm mb-2 ${migrationResults.success ? 'text-green-800' : 'text-red-800'}`}>
                  {migrationResults.message || (migrationResults.success ? 'Migration Results' : 'Migration Failed')}
                </h4>
                
                {migrationResults.results && (
                  <div className="space-y-2 text-xs">
                    {migrationResults.results.users_deleted?.length > 0 && (
                      <div>
                        <span className="font-medium text-green-700">Users Deleted: </span>
                        {migrationResults.results.users_deleted.map(u => u.name || u).join(", ")}
                      </div>
                    )}
                    {migrationResults.results.users_not_found?.length > 0 && (
                      <div>
                        <span className="font-medium text-amber-700">Users Not Found: </span>
                        {migrationResults.results.users_not_found.join(", ")}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Orphaned Blocks {migrationForm.dryRun ? 'to Delete' : 'Deleted'}: </span>
                      {migrationResults.results.orphaned_blocks_deleted || 0}
                    </div>
                    <div>
                      <span className="font-medium">Orphaned Resources {migrationForm.dryRun ? 'to Delete' : 'Deleted'}: </span>
                      {migrationResults.results.orphaned_resources_deleted || 0}
                    </div>
                    <div>
                      <span className="font-medium">Display Names {migrationForm.dryRun ? 'to Fix' : 'Fixed'}: </span>
                      {migrationResults.results.display_names_fixed || 0}
                    </div>
                    <div>
                      <span className="font-medium">Passwords {migrationForm.dryRun ? 'to Migrate' : 'Migrated'}: </span>
                      {migrationResults.results.passwords_migrated || 0}
                    </div>
                    {migrationResults.results.errors?.length > 0 && (
                      <div className="text-red-600">
                        <span className="font-medium">Errors: </span>
                        {migrationResults.results.errors.join(", ")}
                      </div>
                    )}
                  </div>
                )}
                
                {migrationResults.error && (
                  <p className="text-red-600 text-sm">{migrationResults.error}</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMigrationDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={handleRunMigration} 
              disabled={migrationRunning}
              className={migrationForm.dryRun ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}
            >
              {migrationRunning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {migrationForm.dryRun ? "Preview Changes" : "Run Migration"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Users Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Activate Pending Users
            </DialogTitle>
            <DialogDescription>
              Create user accounts from pending invitations or resource records. Users will be able to log in with the temporary password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Source selector */}
            <div className="space-y-2">
              <Label>Activate From</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={activateSource === "invitations" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActivateSource("invitations")}
                  className="flex-1"
                >
                  Pending Invitations
                </Button>
                <Button
                  type="button"
                  variant={activateSource === "resources" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActivateSource("resources")}
                  className="flex-1"
                >
                  Resources (Project Managers)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {activateSource === "invitations" 
                  ? "For users who received invitations but haven't completed signup"
                  : "For Project Managers in the Resource Center without login accounts"}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Names to Activate (comma-separated)</Label>
              <Input
                placeholder="e.g., Sofia, Eugenia"
                value={activateForm.names}
                onChange={(e) => setActivateForm({ ...activateForm, names: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Enter names or emails to activate</p>
            </div>
            
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <Input
                value={activateForm.temporaryPassword}
                onChange={(e) => setActivateForm({ ...activateForm, temporaryPassword: e.target.value })}
                placeholder="TempPass123!"
              />
              <p className="text-xs text-muted-foreground">Users will log in with this password. They should change it after first login.</p>
            </div>
            
            {/* Results display */}
            {activateResults && (
              <div className={`p-4 rounded-lg border ${activateResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`font-semibold text-sm mb-2 ${activateResults.success ? 'text-green-800' : 'text-red-800'}`}>
                  {activateResults.message || (activateResults.success ? 'Activation Results' : 'Activation Failed')}
                </h4>
                
                {activateResults.results && (
                  <div className="space-y-2 text-xs">
                    {activateResults.results.activated?.length > 0 && (
                      <div className="bg-green-100 p-2 rounded">
                        <span className="font-medium text-green-700">✓ Activated: </span>
                        {activateResults.results.activated.map(u => (
                          <div key={u.user_id} className="ml-2">
                            <strong>{u.name}</strong> ({u.email}) - Password: <code className="bg-white px-1 rounded">{u.temporary_password}</code>
                          </div>
                        ))}
                      </div>
                    )}
                    {activateResults.results.not_found?.length > 0 && (
                      <div>
                        <span className="font-medium text-amber-700">Not Found: </span>
                        {activateResults.results.not_found.join(", ")}
                      </div>
                    )}
                    {activateResults.results.already_exists?.length > 0 && (
                      <div>
                        <span className="font-medium text-blue-700">Already Exists: </span>
                        {activateResults.results.already_exists.map(u => u.name || u.email).join(", ")}
                      </div>
                    )}
                    {activateResults.results.errors?.length > 0 && (
                      <div className="text-red-600">
                        <span className="font-medium">Errors: </span>
                        {activateResults.results.errors.join(", ")}
                      </div>
                    )}
                  </div>
                )}
                
                {activateResults.error && (
                  <p className="text-red-600 text-sm">{activateResults.error}</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={handleActivateUsers} 
              disabled={activateRunning || !activateForm.names.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {activateRunning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Activate Users
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
